import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  Home,
  Plus,
  Upload,
  FolderPlus,
  Grid,
  List,
  History,
  Share,
  Download,
  Trash2,
  ExternalLink,
  Copy,
  Pencil,
  Files,
  Link,
  Code,
  FileSearch,
} from 'lucide-react';
import { WorkspaceFile } from '../../types';
import { FileItem } from './FileItem';
import { FolderItem } from './FolderItem';
import { Spinner, MenuItem } from '../Common';
import clsx from 'clsx';

interface FileListProps {
  workspace: string;
  path: string;
  files: WorkspaceFile[];
  loading?: boolean;
  viewMode?: 'list' | 'grid';
  selectedFiles?: string[];
  multiSelectMode?: boolean;
  onNavigate: (path: string) => void;
  onFileSelect?: (path: string) => void;
  onFileOpen?: (file: WorkspaceFile) => void;
  onViewHistory?: (file: WorkspaceFile) => void;
  onShare?: (file: WorkspaceFile) => void;
  onDelete?: (path: string) => void;
  onRename?: (file: WorkspaceFile) => void;
  onCopy?: (file: WorkspaceFile) => void;
  onMove?: (file: WorkspaceFile) => void;
  onGetLink?: (file: WorkspaceFile) => void;
  onGetEmbedCode?: (file: WorkspaceFile) => void;
  onNewFile?: () => void;
  onNewFolder?: () => void;
  onUpload?: () => void;
  getCdnUrl?: (path: string) => string;
}

export function FileList({
  workspace,
  path,
  files,
  loading,
  viewMode = 'list',
  selectedFiles = [],
  multiSelectMode,
  onNavigate,
  onFileSelect,
  onFileOpen,
  onViewHistory,
  onShare,
  onDelete,
  onRename,
  onCopy,
  onMove,
  onGetLink,
  onGetEmbedCode,
  onNewFile,
  onNewFolder,
  onUpload,
  getCdnUrl,
}: FileListProps) {
  const [expandHistory, setExpandHistory] = useState(false);

  // Build breadcrumb
  const pathParts = path.split('/').filter(Boolean);
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    ...pathParts.map((part, i) => ({
      name: part,
      path: '/' + pathParts.slice(0, i + 1).join('/'),
    })),
  ];

  const getFileContextMenu = (file: WorkspaceFile): MenuItem[] => {
    const items: MenuItem[] = [
      {
        label: 'Open',
        onClick: () => onFileOpen?.(file),
      },
    ];

    if (file.type === 'file') {
      items.push(
        {
          label: 'View History',
          icon: <History className="w-4 h-4" />,
          onClick: () => onViewHistory?.(file),
        },
        { label: '', onClick: () => {}, divider: true },
        {
          label: 'Share',
          icon: <Share className="w-4 h-4" />,
          onClick: () => onShare?.(file),
        },
        {
          label: 'Get Link',
          icon: <Link className="w-4 h-4" />,
          onClick: () => onGetLink?.(file),
        },
        {
          label: 'Copy CDN URL',
          icon: <Copy className="w-4 h-4" />,
          onClick: () => {
            const url = getCdnUrl?.(file.path);
            if (url) {
              navigator.clipboard.writeText(url);
            }
          },
        },
        {
          label: 'Get Embed Code',
          icon: <Code className="w-4 h-4" />,
          onClick: () => onGetEmbedCode?.(file),
        },
        { label: '', onClick: () => {}, divider: true },
        {
          label: 'Download',
          icon: <Download className="w-4 h-4" />,
          onClick: () => {
            const url = getCdnUrl?.(file.path);
            if (url) window.open(url, '_blank');
          },
        }
      );
    }

    // Actions that apply to both files and folders
    items.push(
      { label: '', onClick: () => {}, divider: true },
      {
        label: 'Rename',
        icon: <Pencil className="w-4 h-4" />,
        onClick: () => onRename?.(file),
      },
      {
        label: 'Make a Copy',
        icon: <Files className="w-4 h-4" />,
        onClick: () => onCopy?.(file),
      },
      {
        label: 'Move to...',
        icon: <FolderPlus className="w-4 h-4" />,
        onClick: () => onMove?.(file),
      },
      { label: '', onClick: () => {}, divider: true },
      {
        label: 'View on GitHub',
        icon: <ExternalLink className="w-4 h-4" />,
        onClick: () => {
          // TODO: Construct and open GitHub URL
          const githubUrl = `https://github.com/SmartUp-Chile/ws-${workspace}/blob/main${file.path}`;
          window.open(githubUrl, '_blank');
        },
      },
      { label: '', onClick: () => {}, divider: true },
      {
        label: 'Delete',
        icon: <Trash2 className="w-4 h-4" />,
        danger: true,
        onClick: () => onDelete?.(file.path),
      }
    );

    return items;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const folders = files.filter((f) => f.type === 'directory');
  const regularFiles = files.filter((f) => f.type === 'file');

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1 px-4 py-3 border-b bg-gray-50 text-sm">
        {breadcrumbs.map((crumb, i) => (
          <div key={crumb.path} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="w-4 h-4 text-gray-400" />}
            <button
              onClick={() => onNavigate(crumb.path)}
              className={clsx(
                'hover:text-primary-600',
                i === breadcrumbs.length - 1
                  ? 'text-gray-900 font-medium'
                  : 'text-gray-500'
              )}
            >
              {i === 0 ? <Home className="w-4 h-4" /> : crumb.name}
            </button>
          </div>
        ))}
      </div>

      {/* Actions bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <div className="flex items-center gap-2">
          {onNewFile && (
            <button
              onClick={onNewFile}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <Plus className="w-4 h-4" />
              New File
            </button>
          )}
          {onNewFolder && (
            <button
              onClick={onNewFolder}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <FolderPlus className="w-4 h-4" />
              New Folder
            </button>
          )}
          {onUpload && (
            <button
              onClick={onUpload}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
          )}
        </div>

        <div className="text-sm text-gray-500">
          {files.length} items
        </div>
      </div>

      {/* File list */}
      <div className="flex-1 overflow-auto">
        {files.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FolderPlus className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              This folder is empty
            </h3>
            <p className="text-gray-500 mt-1">
              Add files or create folders to get started
            </p>
          </div>
        ) : (
          <div>
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-2 border-b bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
              {multiSelectMode && <div className="w-5" />}
              <div className="w-5" />
              <div className="flex-1">Name</div>
              <div className="w-20">Size</div>
              <div className="w-28 text-right">Modified</div>
              <div className="w-8" />
            </div>

            {/* Folders first */}
            {folders.map((folder) => (
              <FolderItem
                key={folder.path}
                folder={folder}
                selected={selectedFiles.includes(folder.path)}
                multiSelectMode={multiSelectMode}
                onSelect={() => onFileSelect?.(folder.path)}
                onDoubleClick={() => onNavigate(folder.path)}
                onContextMenu={() => getFileContextMenu(folder)}
              />
            ))}

            {/* Files */}
            {regularFiles.map((file) => (
              <FileItem
                key={file.path}
                file={file}
                selected={selectedFiles.includes(file.path)}
                multiSelectMode={multiSelectMode}
                onSelect={() => onFileSelect?.(file.path)}
                onDoubleClick={() => onFileOpen?.(file)}
                onContextMenu={() => getFileContextMenu(file)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Version history toggle */}
      <div className="border-t">
        <button
          onClick={() => setExpandHistory(!expandHistory)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-600 hover:bg-gray-50"
        >
          <span className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Version History
          </span>
          <ChevronRight
            className={clsx(
              'w-4 h-4 transition-transform',
              expandHistory && 'rotate-90'
            )}
          />
        </button>
      </div>
    </div>
  );
}
